import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CHART, money } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, Overline, KpiCard, SectionTitle } from "@/components/common";
import IntelligenceCard from "@/components/IntelligenceCard";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Sparkles, Flag, Timer, DollarSign, UtensilsCrossed, Check, ArrowRight,
  Activity, TrendingUp, ChevronRight, CircleDot,
} from "lucide-react";

const axis = { stroke: "#A1A1AA", fontSize: 11, tickLine: false, axisLine: false };
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12, fontFamily: "Manrope", boxShadow: "0 8px 24px -12px rgba(0,0,0,.15)" } };

const SIGNAL_ICON = { pace: Timer, revenue: DollarSign, fnb: UtensilsCrossed };

function ChartCard({ title, children, sub }) {
  return (
    <Card className="p-6">
      <div className="mb-4"><Overline>{title}</Overline>{sub && <p className="text-xs text-[#A1A1AA] mt-1">{sub}</p>}</div>
      <div className="h-56" style={{ minHeight: 224 }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={224}>{children}</ResponsiveContainer>
      </div>
    </Card>
  );
}

export default function Overview() {
  const { user } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [panel, setPanel] = useState(null); // 'fill' | 'improve'
  const [fill, setFill] = useState(null);
  const [improve, setImprove] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/overview").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const greet = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  };

  const actionSignal = async (key) => {
    setBusy(key);
    await api.post(`/brief/signal/${key}/action`);
    setBusy(null);
    const labels = { pace: "Marshal dispatched to Hole 7", revenue: "Campaign launched", fnb: "F&B recommendation applied" };
    toast.success(labels[key]);
    await load();
  };

  const openFill = async () => {
    setPanel("fill");
    if (!fill) { const { data } = await api.get("/brief/fill-tee-times"); setFill(data); }
  };
  const openImprove = async () => {
    setPanel("improve");
    if (!improve) { const { data } = await api.get("/brief/improve-capture"); setImprove(data); }
  };

  const onSignal = (sig) => {
    if (sig.key === "pace") return actionSignal("pace");
    if (sig.key === "revenue") return openFill();
    if (sig.key === "fnb") return openImprove();
  };

  const onRecAction = async (rec) => {
    const { data: res } = await api.post(`/recommendations/${rec.id}/action`);
    if (rec.type === "pace") { toast.success("Marshal dispatched to Hole 7"); actionSignal("pace"); }
    else if (rec.type === "revenue") toast.success("Campaign created", { description: "Targeting weekday-afternoon golfers." });
    else { toast.success("Opening F&B module"); setTimeout(() => nav("/fnb"), 500); }
    return res;
  };

  if (!data) return <Skeleton />;
  const m = data.metrics;
  const brief = m.brief;
  const health = m.health;

  return (
    <div className="space-y-8">
      {/* 1. Executive Greeting */}
      <div className="fade-up">
        <h1 className="font-display text-4xl font-light tracking-tight text-[#1C1F1E]">
          {greet()}, <span className="font-semibold">{user?.name}</span>
        </h1>
        <p className="text-[#52525B] mt-1.5">Here's what's happening at Monarch Bay today.</p>
      </div>

      {/* 2. TODAY'S BRIEF */}
      <Card className="overflow-hidden fade-up p-0 border-[#0F3F2D]/15" data-testid="todays-brief">
        <div className="sidebar-grain text-white px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Overline className="!text-white/50">Today's Brief</Overline>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#D97706]/20 border border-[#D97706]/40 text-[#F0B45E] text-xs font-semibold uppercase tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F0B45E] pulse-dot" />{brief.status}
              </span>
            </div>
            <div className="text-white/45 text-xs">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {brief.headline_stats.map((s) => (
              <div key={s.label}>
                <div className="font-display text-2xl lg:text-3xl font-semibold font-mono2">{s.value}</div>
                <div className="text-xs text-white/45 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
          {(brief.projected_line || brief.inventory_line) && (
            <div className="mt-4 pt-4 border-t border-white/10 space-y-1 text-sm text-white/60">
              {brief.projected_line && <div data-testid="brief-projected">{brief.projected_line}</div>}
              {brief.inventory_line && <div data-testid="brief-inventory">{brief.inventory_line}</div>}
            </div>
          )}
        </div>

        <div className="p-6">
          <Overline className="!text-[#DC2626]">{brief.signals.length} Items Require Attention</Overline>
          <div className="mt-4 grid md:grid-cols-3 gap-4">
            {brief.signals.map((sig) => {
              const Icon = SIGNAL_ICON[sig.key] || Activity;
              const critical = sig.status === "Critical";
              const c = critical ? "#DC2626" : "#0F3F2D";
              const actioned = sig.state === "actioned";
              return (
                <div key={sig.key} data-testid={`brief-signal-${sig.key}`} className="rounded-xl border border-[#E5E7EB] p-4 flex flex-col">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: c }}>
                      <Icon size={13} />{sig.type}
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border"
                      style={{ color: c, borderColor: `${c}33`, background: `${c}0d` }}>{sig.status}</span>
                  </div>
                  <p className="text-sm font-semibold text-[#1C1F1E] mt-2.5 leading-snug">{sig.headline}</p>
                  <p className="text-xs text-[#52525B] mt-1.5 leading-relaxed flex-1">{sig.detail}</p>
                  <div className="text-xs font-semibold mt-2" style={{ color: c }}>{sig.impact}</div>
                  <div className="mt-3">
                    {actioned ? (
                      <span data-testid={`brief-signal-${sig.key}-done`} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#059669]">
                        <Check size={14} />{sig.key === "pace" ? "Marshal Dispatched" : sig.key === "revenue" ? "Campaign Launched" : "Action Applied"}
                        {sig.actioned_at && <span className="text-[#A1A1AA]">· {sig.actioned_at}</span>}
                      </span>
                    ) : (
                      <button data-testid={`brief-signal-${sig.key}-action`} onClick={() => onSignal(sig)} disabled={busy === sig.key}
                        className="group w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white transition-colors active:scale-95 disabled:opacity-60"
                        style={{ background: c }}>
                        {busy === sig.key ? "Working..." : sig.button}
                        {busy !== sig.key && <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-5 border-t border-[#E5E7EB] flex items-center justify-between flex-wrap gap-2">
            <Overline>Identified Revenue Opportunity</Overline>
            <div className="font-display text-3xl font-bold text-[#0F3F2D] font-mono2" data-testid="brief-opportunity">{brief.opportunity_today}</div>
          </div>
        </div>
      </Card>

      {/* 3. Core KPIs (secondary) */}
      <div>
        <Overline className="mb-3 block">Core Metrics</Overline>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {m.kpis.map((k) => (
            <KpiCard key={k.label} {...k} testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]/g, "-")}`} />
          ))}
        </div>
      </div>

      {/* 4. Live Operations + Course Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-6 lg:col-span-2" data-testid="live-operations">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 text-[#DC2626]"><CircleDot size={14} className="pulse-dot" /></span>
            <Overline>Live Operations</Overline>
            <span className="text-[10px] font-semibold text-[#DC2626] uppercase tracking-wide">Live</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-[#F1F3F2]">
            {m.live_ops.map((o) => (
              <div key={o.label} className="py-3 md:py-0 md:px-4 first:md:pl-0">
                <div className={cn("font-display text-xl font-semibold font-mono2",
                  o.tone === "warn" ? "text-[#D97706]" : o.tone === "info" ? "text-[#2563EB]" : "text-[#1C1F1E]")}>{o.value}</div>
                <div className="text-xs text-[#52525B] mt-0.5 leading-tight">{o.label}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6" data-testid="course-health">
          <div className="flex items-center justify-between mb-4">
            <Overline>Course Health</Overline>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-2xl font-bold text-[#0F3F2D] font-mono2">{health.overall}</span>
              <span className="text-xs text-[#A1A1AA]">/ 100</span>
            </div>
          </div>
          <div className="space-y-2.5">
            {health.subscores.map((s) => {
              const weak = s.score < 75;
              const c = s.score >= 85 ? "#059669" : s.score >= 75 ? "#0F3F2D" : "#D97706";
              return (
                <button key={s.name} data-testid={`health-${s.name.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => nav(s.route)}
                  className="w-full group">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={cn("font-medium group-hover:text-[#0F3F2D] transition-colors", weak ? "text-[#D97706]" : "text-[#52525B]")}>{s.name}</span>
                    <span className="font-mono2 font-semibold" style={{ color: c }}>{s.score}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${s.score}%`, background: c }} />
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* 5. Operational Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Revenue Throughout the Day">
          <AreaChart data={m.revenue_by_hour} margin={{ left: -18, right: 8, top: 8 }}>
            <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0F3F2D" stopOpacity={0.22} /><stop offset="100%" stopColor="#0F3F2D" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1EF" vertical={false} />
            <XAxis dataKey="hour" {...axis} /><YAxis {...axis} tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip {...tip} formatter={(v) => [money(v), "Revenue"]} />
            <Area type="monotone" dataKey="revenue" stroke="#0F3F2D" strokeWidth={2} fill="url(#rev)" />
          </AreaChart>
        </ChartCard>

        <ChartCard title="Tee Sheet Utilization" sub="% of capacity booked by hour">
          <BarChart data={m.utilization} margin={{ left: -18, right: 8, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1EF" vertical={false} />
            <XAxis dataKey="hour" {...axis} /><YAxis {...axis} tickFormatter={(v) => `${v}%`} />
            <Tooltip {...tip} formatter={(v) => [`${v}%`, "Utilization"]} cursor={{ fill: "#0F3F2D08" }} />
            <Bar dataKey="utilization" radius={[4, 4, 0, 0]}>
              {m.utilization.map((e, i) => <Cell key={i} fill={e.utilization >= 90 ? "#0F3F2D" : e.utilization >= 60 ? "#2F7A57" : "#7FB89B"} />)}
            </Bar>
          </BarChart>
        </ChartCard>

        <ChartCard title="Revenue by Department">
          <PieChart>
            <Pie data={m.departments} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={2} strokeWidth={0}>
              {m.departments.map((e, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
            </Pie>
            <Tooltip {...tip} formatter={(v, n) => [money(v), n]} />
          </PieChart>
        </ChartCard>

        <ChartCard title="Pace of Play" sub="Minutes over target, by hole">
          <BarChart data={m.pace_curve} margin={{ left: -18, right: 8, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1EF" vertical={false} />
            <XAxis dataKey="hole" {...axis} /><YAxis {...axis} tickFormatter={(v) => `${v}m`} />
            <Tooltip {...tip} formatter={(v) => [`+${v} min`, "Delay"]} cursor={{ fill: "#DC262608" }} />
            <Bar dataKey="delay" radius={[4, 4, 0, 0]}>
              {m.pace_curve.map((e, i) => <Cell key={i} fill={e.delay >= 6 ? "#DC2626" : e.delay >= 3 ? "#D97706" : "#7FB89B"} />)}
            </Bar>
          </BarChart>
        </ChartCard>
      </div>

      {/* 6. ScratchHQ Intelligence */}
      <div>
        <SectionTitle sub="What's happening, why it matters, and what to do about it."
          right={
            <div className="text-right">
              <Overline>Total Identified Opportunity</Overline>
              <div className="flex items-center gap-3 mt-1 justify-end">
                <button data-testid="opp-today" onClick={() => document.querySelector('[data-testid="todays-brief"]')?.scrollIntoView({ behavior: "smooth" })}
                  className="text-sm font-semibold text-[#0F3F2D] font-mono2 hover:underline">Today {brief.opportunity_today}</button>
                <span className="text-[#D1D5DB]">·</span>
                <button data-testid="opp-month" onClick={() => nav("/revenue")}
                  className="text-sm font-semibold text-[#0F3F2D] font-mono2 hover:underline">Month {brief.opportunity_month}</button>
              </div>
            </div>
          }>
          <span className="flex items-center gap-2"><Sparkles size={20} className="text-[#C7A24A]" />ScratchHQ Intelligence</span>
        </SectionTitle>
        <div className="space-y-4">
          {data.recommendations.map((r) => (
            <IntelligenceCard key={r.id} rec={r} onAction={onRecAction} testid={`overview-rec-${r.type}`} />
          ))}
        </div>
      </div>

      {/* Fill Tee Times panel */}
      <Sheet open={panel === "fill"} onOpenChange={(o) => !o && setPanel(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto bg-white/95 backdrop-blur-2xl" data-testid="fill-tee-times-panel">
          <SheetTitle className="sr-only">Fill Tee Times</SheetTitle>
          <div className="px-6 py-5 sidebar-grain text-white">
            <Overline className="!text-white/50">Revenue Opportunity</Overline>
            <div className="font-display text-2xl font-semibold mt-1">Fill Tomorrow's Afternoon</div>
            <div className="text-white/55 text-sm mt-1">1:20–2:40 PM · {fill?.count || 11} unsold tee times</div>
          </div>
          {fill && (
            <div className="p-6 space-y-6">
              <div className="rounded-xl border border-[#0F3F2D]/15 bg-[#0F3F2D]/[0.04] p-4">
                <Overline className="!text-[#0F3F2D]">Suggested Target Segment</Overline>
                <div className="font-semibold text-[#1C1F1E] mt-1">{fill.target_segment}</div>
                <div className="text-sm text-[#52525B] mt-0.5">{fill.segment_size} golfers historically play weekday afternoons</div>
                <div className="font-mono2 font-semibold text-[#0F3F2D] mt-2">{fill.potential_revenue} potential revenue</div>
              </div>
              <div>
                <Overline>Open Tee Times</Overline>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {fill.slots.map((s, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm">
                      <span className="font-mono2 text-[#1C1F1E]">{s.time}</span>
                      <span className="font-mono2 text-[#52525B]">${s.green_fee}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Overline>Suggested Golfers to Target</Overline>
                <div className="mt-2 space-y-1.5">
                  {fill.suggested_targets.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-[#F1F3F2] last:border-0">
                      <span className="text-[#1C1F1E] font-medium">{t.name}</span>
                      <span className="text-[#A1A1AA] text-xs">{t.segment} · {t.days_since_visit}d ago</span>
                    </div>
                  ))}
                </div>
              </div>
              <Button data-testid="fill-confirm" onClick={async () => { await actionSignal("revenue"); setPanel(null); }}
                className="w-full bg-[#0F3F2D] hover:bg-[#165c42] rounded-lg h-11">Launch Fill Campaign</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Improve Capture panel */}
      <Sheet open={panel === "improve"} onOpenChange={(o) => !o && setPanel(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto bg-white/95 backdrop-blur-2xl" data-testid="improve-capture-panel">
          <SheetTitle className="sr-only">Improve F&B Capture</SheetTitle>
          <div className="px-6 py-5 sidebar-grain text-white">
            <Overline className="!text-white/50">F&B Opportunity</Overline>
            <div className="font-display text-2xl font-semibold mt-1">Improve F&B Capture</div>
            <div className="text-white/55 text-sm mt-1">{improve?.current} today vs {improve?.benchmark} benchmark</div>
          </div>
          {improve && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-[#E5E7EB] p-3"><div className="text-xs text-[#A1A1AA]">Current</div><div className="font-mono2 font-semibold text-[#1C1F1E] mt-0.5">{improve.current}</div></div>
                <div className="rounded-lg border border-[#E5E7EB] p-3"><div className="text-xs text-[#A1A1AA]">Benchmark</div><div className="font-mono2 font-semibold text-[#1C1F1E] mt-0.5">{improve.benchmark}</div></div>
                <div className="rounded-lg border border-[#DC2626]/25 bg-[#DC2626]/[0.03] p-3"><div className="text-xs text-[#A1A1AA]">Gap</div><div className="font-mono2 font-semibold text-[#DC2626] mt-0.5">{improve.gap}</div></div>
              </div>
              <div>
                <Overline>Recommended Actions</Overline>
                <div className="mt-2 space-y-2">
                  {improve.steps.map((s, i) => (
                    <div key={i} className="rounded-xl border border-[#E5E7EB] p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[#1C1F1E]">{s.title}</span>
                        <span className="font-mono2 text-xs font-semibold text-[#0F3F2D]">{s.impact}</span>
                      </div>
                      <p className="text-xs text-[#52525B] mt-1.5 leading-relaxed">{s.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-[#0F3F2D]/15 bg-[#0F3F2D]/[0.04] p-4 flex items-center justify-between">
                <Overline className="!text-[#0F3F2D]">Total Potential</Overline>
                <span className="font-mono2 font-bold text-[#0F3F2D] text-lg">{improve.potential_revenue}</span>
              </div>
              <Button data-testid="improve-confirm" onClick={async () => { await actionSignal("fnb"); setPanel(null); }}
                className="w-full bg-[#0F3F2D] hover:bg-[#165c42] rounded-lg h-11">Apply Recommendation</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const Skeleton = () => (
  <div className="space-y-8 animate-pulse">
    <div className="h-10 w-72 bg-[#EEF1EF] rounded-lg" />
    <div className="h-64 bg-white border border-[#E5E7EB] rounded-xl" />
    <div className="grid grid-cols-6 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-white border border-[#E5E7EB] rounded-xl" />)}</div>
  </div>
);
